import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import Masonry from '@repo/editor/src/components/Masonry/Masonry';
import { ColumnItem } from '@repo/editor/src/components/Masonry/types';
import { Post } from '@repo/editor/src/types';
import { uniques } from '@repo/editor/src/utils';
import { withEventScope } from '@repo/event-processor';
import { secondaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';

import ContentWrapper from 'components/Landing/ContentWrapper/ContentWrapper';
import LinkButton from 'components/LinkButton/LinkButton';
import NoPostsLoaded from 'components/NoPostsLoaded/NoPostsLoaded';
import PostPreview from 'components/PostPreview/PostPreview';
import postApi, { PostsResponse } from 'global/postApi';
import { TEMPLATES_PATH } from 'global/utils/templateParams';
import { postsStoreSelector, usePostsStore } from 'stores/postsStore';
import { FlexRow } from 'styles/layouts';

const fetchPosts = async ({
  take,
  skip,
  tag,
  ids,
  category,
  sort,
}: {
  take: number;
  skip: number;
  tag: string | null;
  ids: string[] | null;
  category: string | null;
  sort: 'popularity' | 'trending' | 'recent' | 'name' | null;
}): Promise<PostsResponse> => {
  if (!(tag || ids?.length)) {
    const response = await postApi.explore({ take, skip });

    if (!response.success) {
      return {
        results: [],
        skip,
        take,
      };
    }

    return response;
  }

  return await postApi.filter({
    take,
    skip,
    tag,
    ids,
    category,
    sort,
  });
};

const Preview = (post: ColumnItem<Post>): JSX.Element => (
  <PostPreview canLike post={post} />
);

const DesignFeedSection: React.FC<
  SliceComponentProps<Content.DesignFeedSectionSlice>
> = ({ slice }) => {
  const getPost = usePostsStore(postsStoreSelector.getPost);
  const addPost = usePostsStore(postsStoreSelector.addPost);
  const [posts, setPosts] = useState<Post[]>([]);
  const fetchState = useRef({
    skip: 0,
    take: 30,
    isFetching: false,
  });
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);

  const { tag, category, sort } = slice.primary;

  const ids = useMemo(
    () =>
      slice.items
        .filter(({ postId }) => typeof postId === 'string')
        .map(({ postId }) => postId) as Array<string>,
    [slice.items]
  );

  const fetch = useCallback(async () => {
    if (fetchState.current.isFetching) {
      return;
    }

    const { skip, take } = fetchState.current;

    setLoading(true);

    fetchState.current.isFetching = true;

    const response = await fetchPosts({
      take,
      skip,
      tag,
      ids,
      category,
      sort,
    });

    fetchState.current.isFetching = false;

    if (!response?.results?.length) {
      if (response?.results) {
        setFinished(true);
      }

      setLoading(false);

      return;
    }

    response.results.forEach((post) => {
      if (!getPost(post.id)) {
        addPost(post);
      }
    });
    setPosts((posts) => uniques([...posts, ...response.results]));

    // we only want to fetch two times
    setFinished(skip > 0 || response.results.length < take);

    fetchState.current.skip += take;

    setLoading(false);
  }, [tag, ids, category, sort, getPost, addPost]);

  useEffect(() => {
    void fetch();

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchState.current.skip = 0;
      setPosts([]);
      setFinished(false);
    };
  }, [fetch]);

  const label =
    slice.primary.moreButtonLabel ||
    (finished || !tag ? 'See all templates' : `More ${tag} templates`);

  const finishedHref = slice.primary.moreButtonFinishedRoute ?? TEMPLATES_PATH;

  return (
    <ContentWrapper background="white" className="brands" customWidth="1345px">
      <Masonry gap={24} items={posts} minColumnWidth={249} render={Preview} />
      {!posts.length && <NoPostsLoaded loading={loading} />}
      {!!posts.length && (
        <>
          <Spacer height="24px" />
          <FlexRow justifyContent="center">
            <LinkButton
              buttonAction={fetch}
              height="40px"
              href={finishedHref}
              isButton={!finished}
              label={label}
              loading={loading}
              theme={secondaryTheme}
              width="auto"
            />
          </FlexRow>
        </>
      )}
    </ContentWrapper>
  );
};

export default withEventScope(DesignFeedSection, { feature: 'designFeed' });
